import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import {
  AppCard,
  AppDatePicker,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import ModuleNewsletterManagerSideBarReports from './ModuleNewsletterManagerSideBarReports';
import NewsletterEntryOptions from '../../../layout/screenSpecifics/Modules/NewsletterManager/NewsletterEntryOptions';
import AppSubmitButton from '../../../layout/components/form/AppSubmitButton';
import colors from '../../../constants/colors';
import Paginator from '../../../layout/components/helpers/Paginator';
import { useIsFirstRender } from '../../../hooks/isFirstRender';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';

const ModuleNewsletterManagerHomeScreen = () => {
  const { moduleBridgeHex, page } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const isFirstRender = useIsFirstRender();
  const global = useSelector((state) => state.global);
  const [moduleData, setModuleData] = useState(null);
  const [newsletterEntries, setNewsletterEntries] = useState();
  const [filters, setFilters] = useState({
    dateStampFrom: Moment().startOf('year').format('DD/MM/YYYY'),
    dateStampTo: Moment().endOf('year').format('DD/MM/YYYY'),
  });

  // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(axiosPath('api_r_user_module_newsletterManager_moduleData_get'), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) setModuleData(r.data);
      })
      .catch((e) => dispatch(ErrorHandler(e, history)));
  }, []);
  // > LOAD MODULE DATA //

  // > LOAD NEWSLETTER ENTRIES
  const handleSearchEntries = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_newsletterManager_entry_search'), {
        moduleBridgeHex,
        payload: filters,
        page,
      })
      .then((r) => {
        if (isMounted.current) {
          setNewsletterEntries(r.data);
        }
      })
      .catch((e) => dispatch(ErrorHandler(e, history)));
  }, [filters, page]);

  const handleChangeFilters = async (payload) => setFilters(payload);

  useEffect(() => {
    handleSearchEntries();
  }, [page]);

  useEffect(() => {
    if (!isFirstRender) {
      if (page == 1) {
        handleSearchEntries();
      } else {
        navigate(
          path({
            name: 'user_module_newsletterManager_home',
            params: {
              moduleBridgeHex,
              page: 1,
            },
          })
        );
      }
    }
  }, [filters]);
  // < LOAD NEWSLETTER ENTRIES

  return (
    <div className="pl-2">
      <BreadcrumbHandler paths={[{ name: moduleData?.moduleName }]} />
      <section className="content">
        <ModuleNewsletterManagerSideBarReports />
        <AppCard
          header={ReactDOMServer.renderToString(
            <Translator file="module-newsletterManager" name="filters" />
          )}
          className="mb-3"
        >
          <Formik
            initialValues={{
              dateStampFrom: filters.dateStampFrom,
              dateStampTo: filters.dateStampTo,
            }}
            onSubmit={handleChangeFilters}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-newsletterManager"
                          name="from"
                        />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-newsletterManager"
                          name="date-from-which-bulletin-generated"
                        />
                      )}
                      locale={global?.urlData?.lang}
                      value={values.dateStampFrom}
                      onChange={(opt) => setFieldValue('dateStampFrom', opt)}
                      dropdownMode="select"
                      showMonthDropdown
                      showYearDropdown
                      isClearable
                      error={errors.dateStampFrom}
                      hasToggleType
                    />
                  </div>
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-newsletterManager" name="to" />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-newsletterManager"
                          name="date-up-which-bulletin-generated"
                        />
                      )}
                      locale={global?.urlData?.lang}
                      value={values.dateStampTo}
                      onChange={(opt) => setFieldValue('dateStampTo', opt)}
                      dropdownMode="select"
                      showMonthDropdown
                      showYearDropdown
                      isClearable
                      error={errors.dateStampTo}
                      hasToggleType
                    />
                  </div>
                </div>
                <AppSubmitButton
                  className="btn btn-sm btn-outline-info float-right"
                  isSubmitting={isSubmitting}
                >
                  <Translator file="module-newsletterManager" name="filter" />
                </AppSubmitButton>
              </form>
            )}
          </Formik>
        </AppCard>
        {newsletterEntries?.entries && (
          <AppCard
            header={`${ReactDOMServer.renderToString(
              <Translator file="module-newsletterManager" name="newsletters" />
            )} (${newsletterEntries?.pagination?.totalCount || 0})`}
          >
            {_.isEmpty(newsletterEntries?.entries) ? (
              <AppNoInfo
                text={ReactDOMServer.renderToString(
                  <Translator
                    file="module-newsletterManager"
                    name="no-bulletins-found"
                  />
                )}
                color={colors.grey500}
                icon={
                  <AppRemixIcon
                    icon="archive"
                    color={colors.grey500}
                    size={42}
                    separation="4px"
                  />
                }
              />
            ) : (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th className="col-lg-4">
                      <Translator file="module-newsletterManager" name="type" />
                    </th>
                    <th className="col-lg-2">
                      <Translator
                        file="module-newsletterManager"
                        name="month"
                      />
                    </th>
                    <th className="col-lg-2">
                      <Translator file="module-newsletterManager" name="from" />
                    </th>
                    <th className="col-lg-2">
                      <Translator file="module-newsletterManager" name="to" />
                    </th>
                    <th className="col-lg-2 text-right">
                      <Translator
                        file="module-newsletterManager"
                        name="options"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {newsletterEntries?.entries.map((ne) => (
                    <tr key={ne.id}>
                      <td>
                        {newsletterEntries?.clientName} {ne.extraSubject}
                      </td>
                      <td>
                        {ne.dateFrom && (
                          <Translator
                            file="user"
                            name={`calendar.month${Moment(
                              ne.dateFrom,
                              'DD/MM/YYYY'
                            ).format('M')}`}
                          />
                        )}
                      </td>
                      <td>{ne.dateFrom}</td>
                      <td>{ne.dateTo}</td>
                      <td className="text-right">
                        <NewsletterEntryOptions newsletterEntry={ne} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {newsletterEntries?.entries?.length > 0 && (
              <Paginator
                pagination={newsletterEntries?.pagination}
                route="user_module_newsletterManager_home"
                params={{
                  moduleBridgeHex,
                  page,
                }}
              />
            )}
          </AppCard>
        )}
      </section>
    </div>
  );
};

export default ModuleNewsletterManagerHomeScreen;
